import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { UPLOAD_ORDERS_STEPS } from './constants';
import { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { UploadFormOption, UploadFormOptionDropdown } from '../components/UploadForm/UploadForm.types';
import { getCountriesOpenForSelling } from 'services/countriesList';
import UploadForm from '../components/UploadForm/UploadForm';
import { ICountryExchangeDetail } from 'types/countries';
import {
  IUploadInfo,
  templateUploadedInfo,
  templateWithErrorsInfo,
  uploadTemplateInfo,
  uploadTemplateInfoWithErrors
} from './content/uploadOrdersInfo';
import { IUploadResults } from 'types/bulkUpload';
import { uploadOrdersTemplate } from 'services/bulkUpload/uploadOrders.service';
import { SummarySubmitSection } from '../UploadCatalog/components/SummarySubmitSection/SummarySubmitSection';
import { AxiosError } from 'axios';
import './UploadOrders.scss';

const UploadOrdersModule = () => {
  const { push } = useHistory();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState<ICountryExchangeDetail | undefined>();
  const [countries, setCountries] = useState<ICountryExchangeDetail[]>([]);
  const [uploadResults, setUploadResults] = useState<IUploadResults | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [errorTemplateURL, setErrorTemplateURL] = useState<string | undefined>();
  const [goBackText, setGoBackText] = useState('');

  const dropdownOptions = useMemo<UploadFormOptionDropdown[]>(
    () => [
      {
        label: 'Pais*',
        options: countries.map(country => ({
          label: country.country || '',
          value: country?.id?.toString() || '',
          handleChange: () => setSelectedCountry(country)
        })),
        type: 'dropdown',
        defaultOption: {
          label: selectedCountry?.country || 'Selecciona el país',
          value: selectedCountry?.id?.toString() || '',
          isFilled: !!selectedCountry
        }
      }
    ],
    [countries, selectedCountry]
  );

  const UploadFormOptions = useMemo<UploadFormOption[]>(() => [...dropdownOptions], [dropdownOptions]);

  const handleSubmitUploadForm = async (file: File) => {
    try {
      const uploadOrdersResponse = await uploadOrdersTemplate(file);
      const errorsString = uploadOrdersResponse?.errors.flatMap(errorGroup =>
        errorGroup.errors.map(err => (typeof err === 'string' ? err : `Error in row ${err.rowIndex}: ${err.message}`))
      );
      setErrors(errorsString || []);
      setUploadResults({
        totalRecords: uploadOrdersResponse?.totalOrders || 0,
        updatedRecords: uploadOrdersResponse?.createdOrders || 0
      });
      setErrorTemplateURL(uploadOrdersResponse?.errorFileUrl);
      nextStep();
    } catch (error: AxiosError | any) {
      const message = error.response?.data?.message
      if (message) {
        const messagesList = typeof message === 'string' ? message.split(',') : message;
        setErrors(messagesList);
        nextStep();
      } else {
        displayErrorNotification({
          customJSXMessage: <>{'Error al subir el catálogo, por favor, inténtelo de nuevo'}</>,
          duration: 5000
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      setCurrentStep(curr => curr - 1);
    }
  };

  const nextStep = () => setCurrentStep(curr => curr + 1);

  const summaryUploadInfo = useMemo<IUploadInfo>(() => {
    const updatedRecords = uploadResults?.updatedRecords || 0;
    const totalRecords = uploadResults?.totalRecords || 0;
    if (errors && errors.length > 0) {
      return templateWithErrorsInfo(updatedRecords, totalRecords);
    }
    return templateUploadedInfo(updatedRecords, totalRecords);
  }, [errors, uploadResults]);

  const uploadInfo = useMemo<IUploadInfo>(() => {
    if (errors && errors.length > 0) {
      return uploadTemplateInfoWithErrors(errorTemplateURL || '');
    }
    return uploadTemplateInfo;
  }, [errors, errorTemplateURL]);

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UploadForm
            key={`step-${step}`}
            options={UploadFormOptions}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            fileInputIsDisabled={!selectedCountry}
            uploadInfo={uploadInfo}
            customClassName="uploadOrdersForm"
          />
        );
      case 1:
        return (
          <UploadForm
            key={`step-${step}`}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            fileInputIsDisabled={!selectedCountry}
            hideAlert
            hideFileInput
            customSubmitSection={
              <SummarySubmitSection
                areErrors={errors && errors.length > 0}
                errorTemplateURL={errorTemplateURL}
                handleReturnToUpload={handleBack}
                typeOfSummary="orders"
              />
            }
            uploadInfo={summaryUploadInfo}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    getCountriesOpenForSelling().then(countries => {
      setCountries(countries);
    });
  }, []);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(`Volver a ${UPLOAD_ORDERS_STEPS[0]}`);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep]);

  return (
    <main className="upload-orders-container">
      <BulkUploadHeader steps={UPLOAD_ORDERS_STEPS} currentStep={currentStep} handleBack={handleBack} goBackText={goBackText} />
      {renderComponentInStep(currentStep)}
    </main>
  );
};

export default UploadOrdersModule;
